"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useUncontrolledQuantityPicker = useUncontrolledQuantityPicker;
var _react = require("react");
var _constants = require("./constants");
/**
 * @param {UseQuantityPickerInnerProps} Props
 * @private
 */
function useUncontrolledQuantityPicker(props) {
  const {
    id,
    initialValue,
    loadOnClick,
    maxValue = _constants.DEFAULT_MAX_VALUE,
    minValue,
    onChange,
    setLastIncreased,
    triggerClickEvent,
    setTooltipMaxOpen
  } = props;
  const [internalValue, setInternalValue] = (0, _react.useState)(initialValue ?? minValue ?? 1);
  const onIncrease = (0, _react.useCallback)(() => {
    triggerClickEvent(id);
    setLastIncreased(id);
    if (typeof internalValue !== 'number' || internalValue === maxValue) {
      return;
    }
    setInternalValue(internalValue + 1);
    onChange?.(internalValue + 1, id, 'increase');
  }, [triggerClickEvent, id, setLastIncreased, internalValue, maxValue, onChange]);
  const onDecrease = (0, _react.useCallback)(() => {
    triggerClickEvent(id);
    setLastIncreased('');
    if (typeof internalValue !== 'number' || internalValue === (minValue ?? _constants.DEFAULT_MIN_VALUE)) {
      return;
    }
    setInternalValue(internalValue - 1);
    onChange?.(internalValue - 1, id, 'decrease');
  }, [triggerClickEvent, id, setLastIncreased, internalValue, minValue, onChange]);

  // in uncontrolled version we use same logic for blur and enter pressed
  const handleInputElement = (0, _react.useCallback)(inputValue => {
    const source = 'input-blur';
    const num = Number.parseInt(inputValue, 10);
    if (!Number.isNaN(num) && num >= (minValue ?? _constants.DEFAULT_MIN_VALUE) && num <= maxValue) {
      setInternalValue(num);
      onChange?.(num, id, source);
      return;
    }
    if (!Number.isNaN(num) && num > maxValue) {
      setTooltipMaxOpen(true);
      setInternalValue(maxValue);
      onChange?.(maxValue, id, source);
      return;
    }
    const fallbackValue = initialValue ?? minValue ?? 1;
    setInternalValue(fallbackValue);
    onChange?.(fallbackValue, id, source);
  }, [id, initialValue, maxValue, minValue, onChange, setTooltipMaxOpen]);
  const handleChange = (0, _react.useCallback)(({
    currentTarget
  }) => {
    triggerClickEvent(id);
    setLastIncreased('');
    if (currentTarget.value === '' || currentTarget.value === '-') {
      setInternalValue(currentTarget.value);
      return;
    }
    const num = Number.parseInt(currentTarget.value, 10);
    if (Number.isNaN(num)) {
      return;
    }
    if (num >= (minValue ?? _constants.DEFAULT_MIN_VALUE) && num <= maxValue) {
      setInternalValue(num);
      onChange?.(num, id, 'input-change');
    }
    if (num > maxValue) {
      setTooltipMaxOpen(true);
      setInternalValue(maxValue);
      onChange?.(maxValue, id, 'input-change');
    }
  }, [triggerClickEvent, id, setLastIncreased, minValue, maxValue, onChange, setTooltipMaxOpen]);
  const handleBlur = (0, _react.useCallback)(({
    currentTarget
  }) => {
    triggerClickEvent(id);
    setLastIncreased(id);
    handleInputElement(currentTarget.value);
  }, [triggerClickEvent, id, setLastIncreased, handleInputElement]);

  /** Set last increased for newly initiated with value grater than zero.
   * In case that the value is set to zero, on next increase will be set as newly initiated again.
   */
  (0, _react.useEffect)(() => {
    if (loadOnClick && typeof internalValue === 'number' && internalValue === 1) {
      setLastIncreased(id);
    }
  }, [id, internalValue, loadOnClick, setLastIncreased]);
  return {
    ...props,
    groupValue: 0,
    handleBlur,
    handleChange,
    id,
    onDecrease,
    onIncrease,
    value: internalValue
  };
}
//# sourceMappingURL=useUncontrolledQuantityPicker.js.map